import React, { useState } from "react";
import { classNames } from "maven-lib/dist/utils/misc";
import { CrButton } from "./CrButton";
import { CrDropdown } from "./CrDropdown";
import { CrIconButton } from "./CrIconButton";
import { CrYearDateLabel } from "./CrYearInput";
import dayjs from "dayjs";

export interface CrYearProps {
  selectedYear?: number
  minDate: Date | number | "TODAY";
  maxDate: Date | number | "TODAY";
  closable: boolean;
  dateLabel?: CrYearDateLabel;

  onDismiss?: () => void;
  onSelectYear?: (year: string) => void;
}

export function CrYear(props: CrYearProps) {
  const {
    selectedYear,
    minDate,
    maxDate,
    dateLabel,
    onSelectYear,
    onDismiss,
    closable,
  } = props;
  const selectedDate: Date | null = (() => {
    if (selectedYear) {
      const c = dayjs();

      return dayjs(
        `${selectedYear || c.year()}-${String(c.month() + 1).padStart(2, "0")}-${String(c.date()).padStart(2, "0")}`
      ).toDate();
    } else {
      return null;
    }
  })();

  const minVal = Math.min(
    selectedDate ? selectedDate.getTime() : 9 * 10 ** 13,
    minDate instanceof Date
      ? minDate.getTime()
      : typeof minDate === "number"
      ? dayjs().subtract(minDate, "days").toDate().getTime()
      : new Date().getTime()
  );

  const maxVal = Math.max(
    selectedDate ? selectedDate.getTime() : 1,
    maxDate instanceof Date
      ? maxDate.getTime()
      : typeof maxDate === "number"
      ? dayjs().add(maxDate, "days").toDate().getTime()
      : new Date().getTime()
  );

  const years: number[] = (() => {
    const min = dayjs(minVal).year();
    const max = dayjs(maxVal).year();

    return "*"
      .repeat(max - min + 1)
      .split("")
      .map((_, i) => min + i)
      .reverse();
  })();

  const [year, setYear] = useState(selectedDate ? dayjs(selectedDate).year() : dayjs(Math.min(maxVal, new Date().getTime())).year());

  return (
    <>
      <div className={classNames("cr-calendar", closable && "--closable")}>
        <div className="cr-calendar-header">
          <div className="before">
            <CrIconButton
              icon="ChevronLeft"
              disabled={year <= Number(dayjs(minVal).format("YYYY"))}
              onClick={() => {
                const prev = dayjs(`${year}-01-01`).subtract(1, "year");

                setYear(prev.year());
              }}
            />
          </div>
          <div className="cr-calendar-navi">
            <div className="year">
              <CrDropdown
                calloutClassName="cr-calendar-navi--year"
                defaultKey={String(year)}
                items={years.map((year) => ({ key: String(year), text: String(year) }))}
                onValueChange={(val) => {
                  setYear(parseInt(val as string, 10));
                }}
              />
              {dateLabel?.year || "년"}
            </div>
          </div>
          <div className="next">
            <CrIconButton
              disabled={year >= Number(dayjs(maxVal).format("YYYY"))}
              onClick={() => {
                const next = dayjs(`${year}-01-01`).add(1, "year");

                setYear(next.year());
              }}
              icon="ChevronRight"
            />
          </div>
          {closable && (
            <div className="close">
              <CrIconButton color="primary" onClick={() => onDismiss && onDismiss()} icon="CalculatorMultiply" />
            </div>
          )}
        </div>
        <CrButton
          className="cr-calendar-enter"
          onClick={() => onSelectYear && onSelectYear(String(year))}
        >
          Enter
        </CrButton>
      </div>
    </>
  );
}

CrYear.defaultProps = {
  minDate: dayjs().subtract(90, "years").toDate(),
  maxDate: dayjs().add(2, "years").toDate(),
  closable: true,
};
